// Numbers, money and dates render in the browser's locale.

export function formatNumber(value: number | null | undefined): string {
  if (value == null) return "-";
  return value.toLocaleString();
}

/**
 * Money in `currency` (an ISO code; null renders the bare amount). By default
 * at least cents and up to 4 decimals, for per-unit prices like $0.0125;
 * `decimals` fixes the count. Empty values render "-".
 */
export function formatMoney(
  value: number | string | null | undefined,
  currency: string | null = "USD",
  { decimals }: { decimals?: number } = {},
): string {
  if (value == null || value === "") return "-";
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return currency ? `${value} ${currency}` : String(value);
  const digits =
    decimals === undefined
      ? { minimumFractionDigits: 2, maximumFractionDigits: 4 }
      : { minimumFractionDigits: decimals, maximumFractionDigits: decimals };
  if (!currency) return n.toLocaleString(undefined, digits);
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency, ...digits }).format(n);
  } catch {
    // Not an ISO 4217 code: show it after the amount.
    return `${n.toLocaleString(undefined, digits)} ${currency}`;
  }
}

/** A large amount at a glance: "NOK 45M", "SEK 1.2B". Empty values render "-". */
export function formatCompactMoney(value: number | null | undefined, currency: string): string {
  if (value == null) return "-";
  const compact = { notation: "compact", maximumFractionDigits: 1 } as const;
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency, ...compact }).format(value);
  } catch {
    return `${new Intl.NumberFormat(undefined, compact).format(value)} ${currency}`;
  }
}

/** File size in B / KB / MB; "" when unknown. */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  const fmt = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 1 });
  if (bytes < 1024 * 1024) return `${fmt(bytes / 1024)} KB`;
  return `${fmt(bytes / (1024 * 1024))} MB`;
}

/** "just now", "5m ago", "3h ago" (up to 48h), "4d ago"; `seconds` shows "12s ago" under a minute. */
export function timeAgo(
  value: string | number | Date,
  { seconds: showSeconds = false }: { seconds?: boolean } = {},
): string {
  const seconds = Math.max(0, Math.floor((Date.now() - toDate(value).getTime()) / 1000));
  if (seconds < 60) return showSeconds ? `${seconds}s ago` : "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export type DateFormat = "date" | "datetime" | "time" | "month" | "full";

const DATE_FORMATS: Record<DateFormat, Intl.DateTimeFormatOptions> = {
  date: { dateStyle: "medium" },
  datetime: { dateStyle: "medium", timeStyle: "short" },
  time: { timeStyle: "medium" },
  month: { year: "numeric", month: "short" },
  full: { dateStyle: "full", timeStyle: "long" },
};

/** A plain `YYYY-MM-DD` is a calendar day: read it as local midnight, not UTC. */
function toDate(value: string | number | Date): Date {
  if (value instanceof Date) return value;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return new Date(`${value}T00:00:00`);
  }
  return new Date(value);
}

/** String form of `<FormattedDate>`, for titles, tooltips and template strings. */
export function formatDate(
  value: string | number | Date | null | undefined,
  format: DateFormat = "date",
  fallback = "-",
): string {
  if (value == null || value === "") return fallback;
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleString(undefined, DATE_FORMATS[format]);
}

/** "awaiting_review" → "Awaiting review": a status or enum value as a label. */
export function humanize(value: string): string {
  const spaced = value.replace(/[_-]+/g, " ").trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Share of `part` in `whole` as "12%", "0.4%" below one percent, "—" without a whole. */
export function percent(part: number, whole: number): string {
  if (whole <= 0) return "—";
  const p = (part / whole) * 100;
  if (p > 0 && p < 1) return `${p.toFixed(1)}%`;
  return `${Math.round(p)}%`;
}

/** `part` as a whole-number percentage of `whole`; 0 without a whole. */
export function percentOf(part: number, whole: number): number {
  return whole > 0 ? Math.round((part / whole) * 100) : 0;
}

/** A chart bucket's day as "Oct 25", read in UTC like the buckets themselves. */
export const utcDayLabel = (t: number) =>
  new Date(t).toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });

/** "SHAREHOLDER_OF" -> "Shareholder Of". */
export function titleCase(value: string): string {
  return value.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}
